import React, { useMemo } from 'react';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { ForwardedHTMLProps } from 'src/tef/components/utils/types';
import { isValidInternalUrl } from 'src/utils/security';

import { CardContextProvider } from './CardContext';

type CardVariant = 'default' | 'elevated' | 'outlined' | 'ghost' | 'filled' | 'compact' | 'ledger';
type CardSpacing = 'none' | 'compact' | 'default' | 'spacious';

interface ContentCardProps extends React.HTMLAttributes<HTMLElement>, ForwardedHTMLProps {
  children: React.ReactNode;
  as?: 'div' | 'article' | 'section' | 'aside' | 'li';
  variant?: CardVariant;
  interactive?: boolean;
  selected?: boolean;
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
  spacing?: CardSpacing;
  noPadding?: boolean;
  fullWidth?: boolean;
  fullHeight?: boolean;
  href?: string;
  className?: string;
}

const spacingClasses: Record<CardSpacing, string> = {
  none: '',
  compact: 'p-3',
  default: 'p-5',
  spacious: 'p-8',
};

export const ContentCard = React.memo(
  React.forwardRef<HTMLElement, ContentCardProps>(
    (
      {
        children,
        as = 'div',
        variant = 'default',
        interactive = false,
        selected = false,
        loading = false,
        disabled = false,
        error = false,
        spacing = 'default',
        noPadding = false,
        fullWidth = false,
        fullHeight = false,
        href,
        className,
        onClick,
        ...props
      },
      ref
    ) => {
      const forwardedProps = forwardProps(props);

      const isClickable = interactive || !!onClick || !!href;

      const cardClasses = useMemo(() => {
        const base = cn(
          'rounded-xl',
          'transition-all duration-200',
          !noPadding && spacingClasses[spacing],
          fullWidth && 'w-full',
          fullHeight && 'h-full'
        );

        const variantClasses: Record<CardVariant, string> = {
          default: cn('bg-white dark:bg-slate-800', 'border border-slate-200 dark:border-slate-700'),
          elevated: cn('bg-white dark:bg-slate-800', 'border border-slate-200 dark:border-slate-700', 'shadow-md'),
          outlined: cn('bg-transparent', 'border border-slate-200 dark:border-slate-700'),
          ghost: cn('bg-transparent', 'border border-transparent'),
          filled: cn('bg-slate-100 dark:bg-slate-800', 'border border-transparent'),
          compact: cn(
            'bg-white dark:bg-slate-900',
            'border border-slate-200 dark:border-slate-800',
            'rounded-md',
            'px-4 py-3'
          ),
          ledger: cn(
            'bg-white dark:bg-slate-900',
            'border-y border-slate-200 dark:border-slate-800',
            'rounded-sm',
            'px-5 py-4'
          ),
        };

        const stateClasses = cn(
          selected && 'ring-2 ring-primary border-primary',
          error && 'border-red-500 ring-1 ring-red-500/20',
          loading && 'animate-pulse',
          disabled && 'opacity-50 pointer-events-none'
        );

        const interactiveClasses =
          isClickable &&
          !disabled &&
          cn(
            'cursor-pointer',
            'hover:bg-slate-50 dark:hover:bg-slate-700/50',
            'hover:border-slate-300 dark:hover:border-slate-600',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
            'active:scale-[0.99]'
          );

        return cn(base, variantClasses[variant], stateClasses, interactiveClasses, className);
      }, [
        variant,
        spacing,
        noPadding,
        fullWidth,
        fullHeight,
        isClickable,
        selected,
        error,
        loading,
        disabled,
        className,
      ]);

      const validatedHref = href && isValidInternalUrl(href) ? href : undefined;
      const Component = validatedHref ? 'a' : as;
      const linkProps = validatedHref ? { href: validatedHref, target: '_blank', rel: 'noopener noreferrer' } : {};

      return (
        <CardContextProvider spacing={spacing}>
          <Component
            ref={ref as React.Ref<never>}
            className={cardClasses}
            onClick={!disabled ? onClick : undefined}
            aria-disabled={disabled}
            aria-busy={loading}
            aria-selected={selected}
            tabIndex={isClickable && !disabled ? 0 : undefined}
            role={isClickable && !validatedHref ? 'button' : undefined}
            {...linkProps}
            {...forwardedProps}
            {...props}
          >
            {children}
          </Component>
        </CardContextProvider>
      );
    }
  )
);

ContentCard.displayName = 'ContentCard';

export default ContentCard;
